import React, { ReactNode, ReactElement, useState, useEffect, useRef } from 'react';
import { FlowContext, FlowContextValue } from './flow-context';

interface FlowStepProps {
    id: number;
    children: ReactNode;
}

// step component that will be rendered by Flow component
const FlowStep: React.FC<FlowStepProps> = ({ id, children }) => {
    const { currentStep } = React.useContext(FlowContext);
    return currentStep === id ? <>{children}</> : null;
};

export interface FlowProps {
    children: ReactNode;
    onComplete?: () => void;
    initialStep?: number;
    scrollToTop?: () => void; // override default behavior described below
}

// main flow component that manages the state of the flow and renders the current step
const FlowComponent: React.FC<FlowProps> = (props) => {
    const { children, onComplete, initialStep = 0, scrollToTop } = props;
    const [currentStep, setCurrentStep] = useState(initialStep);
    const containerRef = useRef<HTMLDivElement>(null);

    const stepIds = React.Children.toArray(children)
        .filter((child): child is ReactElement<FlowStepProps> => React.isValidElement(child) && child.type === FlowStep)
        .map((child) => child.props.id)
        .sort((a, b) => a - b);

    const currentIndex = stepIds.indexOf(currentStep);
    const isFirstStep = currentIndex === 0;
    const isLastStep = currentIndex === stepIds.length - 1;

    const next = () => {
        if (isLastStep) {
            onComplete?.();
        } else {
            setCurrentStep(stepIds[currentIndex + 1]);
        }
    };

    const back = () => {
        if (!isFirstStep) {
            setCurrentStep(stepIds[currentIndex - 1]);
        }
    };

    const goToStep = (id: number) => {
        if (stepIds.includes(id)) {
            setCurrentStep(id);
        }
    };
    // Function to scroll to the top of the flow when the step changes
    // needs to find the nearest scrollable parent of the flow container and scroll that, or window if none found
    useEffect(() => {
        if (scrollToTop) {
            scrollToTop();
        } else {
            let element: HTMLElement | null = containerRef.current;
            while (element && !['auto', 'scroll'].includes(getComputedStyle(element).overflowY)) {
                element = element.parentElement;
            }
            (element ?? window).scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
        }
    }, [currentStep, scrollToTop]);

    const value: FlowContextValue = { next, back, goToStep, currentStep, isFirstStep, isLastStep };

    // renders own context provider, won't interfere with other Flow components
    return (
        <div ref={containerRef} style={{ display: 'contents' }}>
            <FlowContext.Provider value={value}>{children}</FlowContext.Provider>
        </div>
    );
};

export const Flow = Object.assign(FlowComponent, { Step: FlowStep });
